import { useEffect } from "react";

import { useAppContext } from "@/hooks/use-app-context";

import {
  canEditRosterOrg,
  TeamRosterData,
  useTeamRoster,
} from "../api/teamRoster";
import { TeamSide } from "../constants/teams";
import { GameRosters } from "../types/roster";

const toGameRosters = (
  teams: Record<TeamSide, { name: string }>,
  rosters: Record<TeamSide, TeamRosterData | undefined>,
  canEditOrg: (orgId: string) => boolean,
): GameRosters => {
  const roster = (side: TeamSide) => {
    const data = rosters[side];
    // Loading: nothing to add players to yet
    if (!data) {
      return { rosterName: teams[side].name, players: [], canEdit: false };
    }
    const rosterName = data.roster.rosterName || teams[side].name;
    // Without a team record for the external id the roster can't
    // be edited, but its players still play
    if (!data.record) {
      return { ...data.roster, rosterName, canEdit: false };
    }
    // Omnibus only adds players to unofficial rosters, whose league is the
    // owning org's id
    const { orgId } = data.record;
    if (!orgId.startsWith("ORG#")) {
      return { ...data.roster, rosterName, canEdit: false, isOfficial: true };
    }
    return { ...data.roster, rosterName, orgId, canEdit: canEditOrg(orgId) };
  };
  return { away: roster("away"), home: roster("home") };
};

/** Both teams' omnibus roster queries, shared by every caller's cache. */
export const useTeamRosters = (
  teamIds: Partial<Record<TeamSide, string>> | undefined,
) => {
  const { userOrganizations } = useAppContext();
  const userOrgIds = userOrganizations.map((org) => org.orgId);
  return {
    away: useTeamRoster(teamIds?.away, userOrgIds),
    home: useTeamRoster(teamIds?.home, userOrgIds),
  };
};

/**
 * Whether either team's roster is on its first load. The game page waits for
 * them, so the lineup panel and player pickers never open on empty rosters.
 * A failed load doesn't count: the game opens without them, and tagging
 * never waits on omnibus rosters. Later reloads don't count either.
 */
export const useIsLoadingGameRosters = (
  teamIds: Record<TeamSide, string> | undefined,
) => {
  const { away, home } = useTeamRosters(teamIds);
  return away.isLoading || home.isLoading;
};

/** Both teams' rosters before omnibus's load: named, with no players */
export const toLoadingRosters = (teams: Record<TeamSide, { name: string }>) =>
  toGameRosters(teams, { away: undefined, home: undefined }, () => false);

/**
 * Both teams' omnibus rosters. They replace the loading rosters and refresh
 * the game's local ones whenever they reload.
 */
export const useGameRosters = ({
  teamIds,
  teamNames,
  setRosters,
}: {
  teamIds: Record<TeamSide, string> | undefined;
  /** Labels a roster that's loading or missing */
  teamNames: Record<TeamSide, { name: string }>;
  setRosters: (rosters: GameRosters) => void;
}) => {
  const { user, userOrganizations } = useAppContext();
  const { away: awayRoster, home: homeRoster } = useTeamRosters(teamIds);

  useEffect(() => {
    // No team ids: no omnibus rosters to load
    if (!teamIds) return;
    const rosters = { away: awayRoster.data, home: homeRoster.data };
    setRosters(
      toGameRosters(teamNames, rosters, (orgId) =>
        canEditRosterOrg(orgId, user?.roles ?? [], userOrganizations),
      ),
    );
    // Team names only label a missing roster; don't re-sync on their change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    awayRoster.data,
    homeRoster.data,
    setRosters,
    teamIds,
    user?.roles,
    userOrganizations,
  ]);

  return {
    rosters: { away: awayRoster.data, home: homeRoster.data },
    isLoading: awayRoster.isLoading || homeRoster.isLoading,
  };
};
